import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import {
  createTicket,
  getAllEnquiries,
  getAllTickets,
  Ticket,
  rejectEnquiry,
  holdEnquiry,
  updateEnquiry,
} from "../../core/api/useTickets";
import { fetchProductList, ModuleType } from "../../core/api/useCompanies";
import {
  Modal,
  Button,
  Select,
  Input,
  Space,
  Form,
  DatePicker,
  Menu,
  Dropdown,
  Row,
  Col,
  Tag,
  Popover,
  Drawer,
} from "antd";
import {
  FileExcelOutlined,
  ExportOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { fetchModules, TableEnquiryRow } from "../../core/api/useEnquiryStatus";
import useGetEmployees, { Employee } from "../../core/api/useGetEmployees";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  getAllTasks,
  Task,
  createTask,
  TaskPayload,
} from "../../core/api/usetasks";
import moment from "moment";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import {
  exportSingleTicketExcel,
  exportAllTicketsExcel,
} from "../../core/api/useTicketExport";
import { usePermission } from "../../core/api/useRolePermission"; // Added import for permission hook
import { CategoryResponse, getAllCategories } from "../../core/api/category";

const { Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const statusColors: {
  [key: string]: { color: string; icon: React.ReactNode };
} = {
  "In Progress": { color: "#d63384", icon: "🛈" },
  "Pending To Assign": { color: "#fd7e14", icon: "⚠️" },
  Rejected: { color: "#dc3545", icon: "⛔" },
  "On Hold": { color: "#6f42c1", icon: "⏸️" },
  Completed: { color: "#198754", icon: "✅" },
  Pending: { color: "#ffc107", icon: "⌛" },
  "Yet To start": { color: "#0d6efd", icon: "✔️" },
  "Ticket Raised": { color: "#0d6efd", icon: "🎫" },
  "Task Created": { color: "#198754", icon: "📝" },
};

const TmsTickets: React.FC = () => {
  const EmpId = useSelector((state: any) => state.auth.employeeId);
  const empRole = useSelector((state: any) => state.auth.role);
  const empToken = useAuthToken();
  const routes = all_routes;
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState<any>(null);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentRequirement, setCurrentRequirement] = useState<string>("");
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const { employees, isLoading, error } = useGetEmployees(empToken);

  const [activeTab, setActiveTab] = useState<"tickets" | "enquiry">("tickets");
  const [addTaskModalVisible, setAddTaskModalVisible] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);

  const [ticketForm] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);

  const [showHoldReason, setShowHoldReason] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [ticketEnquiryId, setTicketEnquiryId] = useState<number | null>(null);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const empDept = useSelector((state: any) => state.auth.empDept);
  const [raisedTicketModalVisible, setRaisedTicketModalVisible] =
    useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [searchText, setSearchText] = useState("");
  const [ticketDetailsModalVisible, setTicketDetailsModalVisible] =
    useState(false);
  const [selectedTicketForDetails, setSelectedTicketForDetails] =
    useState<Ticket | null>(null);
  // Add state and effect for categories
  const [categories, setCategories] = useState<CategoryResponse[]>([]);

  // Permissions
  const canEditEnquiries = usePermission("Enquiries", "write");
  const canCreateTasks = usePermission("Tasks", "create");

  // Allow both Manager and Incharge roles
  const roleFilters = ["Manager", "Incharge"];

  const filteredEmployees = (employees || []).filter((emp) =>
    roleFilters.some(
      (role) => emp.role_name?.toLowerCase() === role.toLowerCase()
    )
  );

  const filteredDevelopmentEmployees = (employees || []).filter((emp) => {
    const role = emp.role_name || emp.role_name || "";
    const dept = emp.department_name || emp.department_name || "";

    return (
      role.toLowerCase() === "employee" && dept.toLowerCase() === "development"
    );
  });

  const [ticketsWithTasks, setTicketsWithTasks] = useState<string[]>([]);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllEnquiries(empToken);
      setEnquiries(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllTickets(empToken);
      setTickets(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const data = await getAllTasks(empToken);
      setTasks(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const data = await fetchProductList(empToken);
      setProducts(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadProducts();
    if (activeTab === "tickets") {
      loadTickets();
      loadTasks();
    } else {
      loadEnquiries();
    }
  }, [activeTab, loadTickets, loadTasks, loadEnquiries, loadProducts]);

  useEffect(() => {
    const fetchCategories = async () => {
      const data = await getAllCategories(empToken);
      // ✅ Always make sure it's an array
      setCategories(Array.isArray(data) ? data : []);
    };
    fetchCategories();
  }, [empToken]);

  // Memoized category map
  const categoryMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (Array.isArray(categories)) {
      categories.forEach((cat) => {
        map[cat.cate_id] = cat.cate_name;
      });
    }
    return map;
  }, [categories]);

  const handleViewTicketDetails = (ticket: Ticket) => {
    setSelectedTicketForDetails(ticket);
    setTicketDetailsModalVisible(true);
  };

  const resetModalsAndForms = () => {
    setUpdateModalVisible(false);
    setAssignModalVisible(false);
    setAddTaskModalVisible(false);
    setSelectedEnquiry(null);
    setSelectedTask(null);
    setTicketEnquiryId(null);
    setShowRejectReason(false);
    setShowHoldReason(false);
    setRejectionReason("");
    setHoldReason("");
    setCurrentRequirement("");
    ticketForm.resetFields();
    taskForm.resetFields();
  };

  const handleTabClick = (tab: "tickets" | "enquiry") => {
    resetModalsAndForms();
    setActiveTab(tab);
  };

  useEffect(() => {
    if (addTaskModalVisible && ticketId) {
      const matchedTicket = tickets.find(
        (ticket) => ticket.ticket_id === ticketId
      );

      taskForm.setFieldsValue({
        ticket_id: ticketId,
        task_name: matchedTicket?.ticket_title ?? "",
        category: matchedTicket?.enquiry?.category
          ? categoryMap[matchedTicket.enquiry.category] || ""
          : "",
      });
    }
  }, [addTaskModalVisible, ticketId, taskForm, tickets,categoryMap]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [enquiriesData, productsData] = await Promise.all([
          getAllEnquiries(empToken),
          fetchProductList(empToken),
        ]);
        setEnquiries(enquiriesData);
        setProducts(productsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Memoized product lookup map
  const productMap = useMemo(() => {
    if (!Array.isArray(products)) return {}; // safety check
    const map: Record<number, { name: string; modules: string }> = {};
    for (const product of products) {
      const modulesArray = Array.isArray(product.modules)
        ? product.modules
        : [];
      map[product.product_id] = {
        name: product.product_name,
        modules: modulesArray.map((m: ModuleType) => m.module_name).join(","),
      };
    }
    return map;
  }, [products]);

  const moduleMap = useMemo(() => {
    if (!Array.isArray(products)) return {}; // safety check
    const map: Record<number, string> = {};
    products.forEach((product) => {
      const modulesArray = Array.isArray(product.modules)
        ? product.modules
        : [];
      modulesArray.forEach((module: ModuleType) => {
        map[module.module_id] = module.module_name;
      });
    });
    return map;
  }, [products]);

  const handleUpdateClick = useCallback((record: any) => {
    setSelectedEnquiry(record);
    const normalizedStatus = record.status?.trim().toLowerCase();

    setShowRejectReason(false);
    setShowHoldReason(false);
    setRejectionReason("");
    setHoldReason("");

    if (normalizedStatus === "pending" || normalizedStatus === "on hold") {
      setUpdateModalVisible(true);
      setTicketEnquiryId(record.enquiry_id);
    } else if (normalizedStatus === "rejected") {
      setUpdateModalVisible(true);
    } else if (normalizedStatus === "pending to assign") {
      setTicketEnquiryId(record.enquiry_id);
      setAssignModalVisible(true);
    } else if (normalizedStatus === "yet to start") {
      setUpdateModalVisible(true);
    }
  }, []);

  const handleIssueClick = useCallback((record: any) => {
    const status = record.status?.trim().toLowerCase();

    setSelectedEnquiry(record);
    setTicketEnquiryId(record.enquiry_id);

    setShowRejectReason(false);
    setShowHoldReason(false);
    setRejectionReason("");
    setHoldReason("");

    if (status === "pending") {
      setAssignModalVisible(true);
      setUpdateModalVisible(false);
    } else {
      setUpdateModalVisible(true);
      setAssignModalVisible(false);
    }
  }, []);

  useEffect(() => {
    if (assignModalVisible && ticketEnquiryId) {
      const matchedEnquiry = enquiries.find(
        (enq) => enq.enquiry_id === ticketEnquiryId
      );

      ticketForm.setFieldsValue({
        enquiry_id: ticketEnquiryId,
        description: matchedEnquiry?.requirement || "",
        category: matchedEnquiry?.category ? categoryMap[matchedEnquiry.category] || "" : "",
      });
    }
  }, [assignModalVisible, ticketEnquiryId, enquiries, ticketForm]);

  const handleApprove = useCallback(async () => {
    if (!canEditEnquiries) {
      toast.warning("You don't have permission");
      return;
    }
    if (!selectedEnquiry?.enquiry_id) {
      toast.error("Invalid enquiry selected.");
      return;
    }

    try {
      await updateEnquiry(selectedEnquiry.enquiry_id, empToken);
      const updatedEnquiries = await getAllEnquiries(empToken);
      setEnquiries(updatedEnquiries);
      setTicketEnquiryId(selectedEnquiry.enquiry_id);
      setUpdateModalVisible(false);
      setAssignModalVisible(true);
    } catch (error) {
      console.error("Error while approving enquiry:", error);
    }
  }, [selectedEnquiry, canEditEnquiries]);

  const handleReject = useCallback(() => {
    if (!canEditEnquiries) {
      toast.warning("You don't have permission");
      return;
    }
    setShowRejectReason(true);
  }, [canEditEnquiries]);

  const handleHold = () => {
    if (!canEditEnquiries) {
      toast.warning("You don't have permission");
      return;
    }
    setShowRejectReason(false);
    setShowHoldReason(true);
    setRejectionReason("");
  };

  const employeeNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    employees.forEach((emp) => {
      map[emp.employee_id] = `${emp.firstname} ${emp.lastname}`;
    });
    return map;
  }, [employees]);

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      const valuesToSearch = [
        ticket.ticket_id,
        ticket.enquiry?.company_name,
        ticket.enquiry?.company_license,
        productMap[ticket.enquiry?.product_id || -1]?.name,
        moduleMap[ticket.enquiry?.module_id || -1],
        ticket.Status,
        ticket.priority,
        employeeNameMap[ticket.assigned_to],
        employeeNameMap[ticket.created_by],
        ticket.created_at,
      ];

      return valuesToSearch.some((val) => {
        if (val === undefined || val === null) return false;
        return val.toString().toLowerCase().includes(searchText.toLowerCase());
      });
    });
  }, [tickets, searchText, productMap, moduleMap, employeeNameMap]);

  const filteredEnquiries = useMemo(() => {
    return enquiries.filter((enq) => {
      const valuesToSearch = [
        enq.company_name,
        enq.company_license,
        productMap[enq.product_id]?.name,
        moduleMap[enq.module_id],
        enq.enquiry_type,
        enq.status,
        enq.created_at,
      ];
      return valuesToSearch
        .filter(Boolean)
        .some((val) =>
          val.toString().toLowerCase().includes(searchText.toLowerCase())
        );
    });
  }, [enquiries, searchText, productMap, moduleMap]);

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        key: "serial",
        // fixed: "left",
        width: 50,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: "Company Name",
        dataIndex: "company_name",
        key: "company_name",
        fixed: "left",
        width: 370,
        sorter: (a: any, b: any) =>
          (a.company_name || "").localeCompare(b.company_name || ""),
        render: (text: string) =>
          text && typeof text === "string" ? text.toUpperCase() : "-",
      },
      {
        title: "Company License",
        dataIndex: "company_license",
        key: "company_license",
        width: 300,
        sorter: (a: any, b: any) =>
          (a.company_license || "").localeCompare(b.company_license || ""),
        render: (text: string) =>
          text && text.trim().length > 0 ? text : "Not updated",
      },
      {
        title: "Product",
        dataIndex: "product_id",
        key: "product_id",
        width: 320,
        sorter: (a: any, b: any) =>
          (productMap[a.product_id]?.name || "").localeCompare(
            productMap[b.product_id]?.name || ""
          ),
        render: (id: number) => productMap[id]?.name || "N/A",
      },
      {
        title: "Module",
        key: "modules",
        width: 500,
        render: (_: any, record: TableEnquiryRow) => {
          if (record.module_id === null) return "N/A";
          return moduleMap[record.module_id] || "N/A";
        },
        sorter: (a: TableEnquiryRow, b: TableEnquiryRow) => {
          const modA = a.module_id ? moduleMap[a.module_id] || "" : "";
          const modB = b.module_id ? moduleMap[b.module_id] || "" : "";
          return modA.localeCompare(modB);
        },
      },
      {
        title: "Enquiry Type",
        dataIndex: "enquiry_type",
        key: "enquiry_type",
        width: 200,
        sorter: (a: any, b: any) =>
          (a.enquiry_type || "").localeCompare(b.enquiry_type || ""),
        render: (type: string, record: any) => {
          const normalized = type?.trim().toLowerCase();
          const isUpdate = normalized === "update";
          const backgroundColor = isUpdate ? "#1763ad" : "red";

          return (
            <span
              className="text-white rounded px-2 py-1"
              onClick={() => {
                const status = record.status?.trim().toLowerCase();
                const type = record.enquiry_type?.trim().toLowerCase();
                setSelectedEnquiry(record);
                setTicketEnquiryId(record.enquiry_id);

                const openUpdateModalSafely = () => {
                  setTimeout(() => {
                    setUpdateModalVisible(true);
                  }, 0);
                };

                if (type === "update") {
                  if (status === "pending to assign") {
                    setAssignModalVisible(true);
                  } else {
                    openUpdateModalSafely();
                  }
                } else if (type === "issues" || type === "issue") {
                  handleIssueClick(record);
                }
              }}
              style={{
                backgroundColor,
                fontSize:
                  window.innerWidth < 576
                    ? "10px"
                    : window.innerWidth < 768
                    ? "11px"
                    : "12px",
                cursor: "pointer",
              }}
            >
              {isUpdate ? "Update" : "Issue"}
            </span>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        sorter: (a: any, b: any) =>
          (a.status || "").localeCompare(b.status || ""),
        render: (text: string) => {
          const statusObj = statusColors[text] || {
            color: "#0d6efd",
            icon: "✔️",
          };
          return (
            <Tag
              bordered
              style={{
                border: `1px solid ${statusObj.color}`,
                color: statusObj.color,
                backgroundColor: "transparent",
                textTransform: "capitalize",
                fontSize:
                  window.innerWidth < 576
                    ? "10px"
                    : window.innerWidth < 768
                    ? "11px"
                    : "12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "16px",
                padding: "2px 10px",
              }}
            >
              <span>{statusObj.icon}</span>
              <span>{text || "Pending"}</span>
            </Tag>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        width: 100,
        sorter: (a: any, b: any) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        render: (text: string) => (
          <span>{moment(text).format("YYYY-MM-DD HH:mm")}</span>
        ),
      },
      {
        title: "Work Category",
        key: "category",
        width: 200,
        render: (_: any, record: Ticket) => {
          const categoryId = record.category;
          const categoryName =
            typeof categoryId === "number" && categoryMap[categoryId]
              ? categoryMap[categoryId]
              : "N/A";

          const categoryColorMap: Record<string, string> = {
            Billable: "bg-success-transparent",
            "Non-Billable": "bg-warning-transparent",
            "Future Development": "bg-info-transparent",
            "Fame of Company": "bg-pink-transparent",
            "N/A": "bg-secondary-transparent",
          };

          const badgeClass =
            categoryColorMap[categoryName] || "bg-secondary-transparent";

          return (
            <span
              className={`badge ${badgeClass}`}
              style={{ textTransform: "capitalize" }}
            >
              {categoryName}
            </span>
          );
        },
        sorter: (a: any, b: any) =>
          (categoryMap[a.enquiry?.category] || "").localeCompare(
            categoryMap[b.enquiry?.category] || ""
          ),
      },
    ],
    [productMap, handleUpdateClick, handleIssueClick]
  );

  const assignedToColumn = {
    title: "Assigned To",
    dataIndex: "assigned_employee",
    key: "assigned_to",
    width: 250,
    sorter: (a: any, b: any) =>
      (employeeNameMap[a.assigned_to] || "").localeCompare(
        employeeNameMap[b.assigned_to] || ""
      ),
    render: (id: string) => employeeNameMap[id] || id,
  };

  const columns1 = [
    // {
    //   title: "S.No",
    //   key: "serial",
    //   width:100,
    //   render: (_: any, __: any, index: number) => index + 1,
    // },
    {
      title: "Ticket ID",
      dataIndex: "ticket_id",
      width: 170,
      fixed: "left",
      key: "ticket_id",
      sorter: (a: any, b: any) => a.ticket_id.localeCompare(b.ticket_id),
      render: (ticketId: string, record: Ticket) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{ticketId}</span>
        </div>
      ),
    },
    {
      title: "Company Name",
      dataIndex: ["enquiry", "company_name"],
      key: "company_name",
      width: 400,
      sorter: (a: any, b: any) =>
        (a.enquiry?.company_name || "").localeCompare(
          b.enquiry?.company_name || ""
        ),
      render: (text: string) =>
        text && typeof text === "string" ? text.toUpperCase() : "-",
    },
    {
      title: "Product",
      key: "product",
      width: 200,
      render: (_: any, record: Ticket) => {
        const productId = record.enquiry?.product_id;
        return productId !== undefined && productMap[productId]
          ? productMap[productId].name
          : "N/A";
      },
      sorter: (a: Ticket, b: Ticket) => {
        const nameA = a.enquiry
          ? productMap[a.enquiry.product_id]?.name || ""
          : "";
        const nameB = b.enquiry
          ? productMap[b.enquiry.product_id]?.name || ""
          : "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Module",
      key: "module",
      width: 200,
      render: (_: any, record: Ticket) => {
        const moduleId = record.enquiry?.module_id;
        return moduleId !== undefined && moduleMap[moduleId]
          ? moduleMap[moduleId]
          : "N/A";
      },
      sorter: (a: Ticket, b: Ticket) => {
        const modA = a.enquiry ? moduleMap[a.enquiry.module_id] || "" : "";
        const modB = b.enquiry ? moduleMap[b.module_id] || "" : "";
        return modA.localeCompare(modB);
      },
    },
    ...(empRole?.toLowerCase() === "admin" ? [assignedToColumn] : []),
    {
      title: "Created By",
      dataIndex: "creator_employee",
      key: "created_by",
      width: 200,
      sorter: (a: any, b: any) =>
        (employeeNameMap[a.created_by] || "").localeCompare(
          employeeNameMap[b.created_by] || ""
        ),
      render: (id: string) => employeeNameMap[id] || id,
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "status",
      width: 200,
      sorter: (a: any, b: any) =>
        (a.Status || "").localeCompare(b.Status || ""),
      render: (text: string) => {
        const statusObj = statusColors[text] || {
          color: "#0d6efd",
          icon: "✔️",
        };
        return (
          <Tag
            bordered
            style={{
              border: `1px solid ${statusObj.color}`,
              color: statusObj.color,
              backgroundColor: "transparent",
              textTransform: "capitalize",
              fontSize:
                window.innerWidth < 576
                  ? "10px"
                  : window.innerWidth < 768
                  ? "11px"
                  : "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "16px",
              padding: "2px 10px",
            }}
          >
            <span>{statusObj.icon}</span>
            <span>{text || "Pending"}</span>
          </Tag>
        );
      },
    },
    {
      title: "Work Category",
      key: "category",
      width: 200,
      render: (_: any, record: Ticket) => {
        const categoryId = record.enquiry?.category;
        const categoryName =
          typeof categoryId === "number" && categoryMap[categoryId]
            ? categoryMap[categoryId]
            : "N/A";

        const categoryColorMap: Record<string, string> = {
          Billable: "bg-success-transparent",
          "Non Billable": "bg-warning-transparent",
          "Future Development": "bg-info-transparent",
          "Fame of Company": "bg-pink-transparent",
          "N/A": "bg-secondary-transparent",
        };

        const badgeClass =
          categoryColorMap[categoryName] || "bg-secondary-transparent";

        return (
          <span
            className={`badge ${badgeClass}`}
            style={{ textTransform: "capitalize" }}
          >
            {categoryName}
          </span>
        );
      },
      sorter: (a: any, b: any) =>
        (categoryMap[a.enquiry?.category] || "").localeCompare(
          categoryMap[b.enquiry?.category] || ""
        ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 200,
      sorter: (a: any, b: any) =>
        (a.priority || "").localeCompare(b.priority || ""),
      render: (priority: string) => {
        const colorMap = {
          High: "red",
          Medium: "orange",
          Low: "green",
        };
        return (
          <Tag color={colorMap[priority as keyof typeof colorMap]}>
            {priority}
          </Tag>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      sorter: (a: any, b: any) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (text: string) => (
        <span>{moment(text).format("YYYY-MM-DD HH:mm")}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_: any, record: Ticket) => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Popover
            content="Ticket Details"
            title=""
            placement="top"
            trigger="hover"
            overlayInnerStyle={{
              backgroundColor: "#b7a4dcff",
              color: "white",
              border: "none",
              borderRadius: "4px",
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleViewTicketDetails(record);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "#e6f4ff",
                cursor: "pointer",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#bae7ff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#e6f4ff")
              }
            >
              <EyeOutlined style={{ fontSize: 14, color: "#1890ff" }} />
            </span>
          </Popover>
          <Button
            type="default"
            size="small"
            disabled={record.Status !== "Ticket Raised"}
            onClick={(e) => {
              e.stopPropagation();
              if (!canCreateTasks) {
                toast.warning("You don't have permission");
                return;
              }
              setSelectedTask(null);
              taskForm.setFieldsValue({ ticket_id: record.ticket_id });

              if (record.enquiry) setSelectedEnquiry(record.enquiry);

              setTicketId(record.ticket_id || null);
              setSelectedTicket(record);

              setAddTaskModalVisible(true);
              setRaisedTicketModalVisible(false);
            }}
            style={{
              padding: "3px 10px",
              borderRadius: "6px",
              border: "none",
              background:
                record.Status === "Ticket Raised"
                  ? "linear-gradient(90deg, #1890ff, #40a9ff)"
                  : "#f5f5f5",
              color: record.Status === "Ticket Raised" ? "#fff" : "#aaa",
              fontWeight: 500,
              fontSize: "12px",
              cursor:
                record.Status === "Ticket Raised" ? "pointer" : "not-allowed",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) => {
              if (record.Status === "Ticket Raised")
                e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (record.Status === "Ticket Raised")
                e.currentTarget.style.opacity = "1";
            }}
          >
            <i className="ti ti-plus" style={{ fontSize: 12 }} /> Add Task
          </Button>
        </div>
      ),
    },
  ];

  const [modules, setModules] = useState<ModuleType[]>([]);
  useEffect(() => {
    fetchModules(empToken)
      .then((data) => setModules(data))
      .catch((err) => console.error("Error loading modules", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const data = await getAllTickets(empToken);
        setTickets(data);
      } catch (error) {}
    };

    fetchTickets();
  }, []);

  const refreshTickets = async () => {
    try {
      setLoading(true); // start loader
      const data = await getAllTickets(empToken); // fetch tickets from API

      if (Array.isArray(data)) {
        setTickets(data); // update tickets state with fresh data
      } else {
        setTickets([]); // fallback if no array data
        toast.warning("No tickets found");
      }
    } catch (error) {
      console.error(error);
      setTickets([]); // clear tickets on error
    } finally {
      setLoading(false); // stop loader
    }
  };

  useEffect(() => {
    refreshTickets(); // initial load

    const intervalId = setInterval(() => {
      refreshTickets();
    }, 2000);

    return () => clearInterval(intervalId); // cleanup on unmount
  }, []);

  const refreshEnquiries = async () => {
    try {
      setLoading(true); // start loader
      const data = await getAllEnquiries(empToken); // fetch enquiries from API

      if (Array.isArray(data)) {
        setEnquiries(data); // update enquiries state with fresh data
      } else {
        setEnquiries([]); // fallback if no array data
        toast.warning("No enquiries found");
      }
    } catch (error) {
      console.error(error);
      setEnquiries([]); // clear enquiries on error
    } finally {
      setLoading(false); // stop loader
    }
  };

  useEffect(() => {
    refreshEnquiries(); // initial load

    const intervalId = setInterval(() => {
      refreshEnquiries(); // auto refresh every 2 seconds
    }, 2000);

    return () => clearInterval(intervalId); // cleanup on unmount
  }, []);

  const handleAddTicketSubmit = async (values: any) => {
    try {
      const ticketPayload = {
        ...values,
        created_by: EmpId,
      };
      await createTicket(ticketPayload, empToken);
      const updatedEnquiries = await getAllEnquiries(empToken);
      setEnquiries(updatedEnquiries);
      setAssignModalVisible(false);
      setUpdateModalVisible(false);
      setSelectedEnquiry(null);
      setTicketEnquiryId(null);
      setShowRejectReason(false);
      setRejectionReason("");
      ticketForm.resetFields();
      fetchTickets(); //Refresh tickets after creation
    } catch (err) {
      console.error("Failed to assign ticket:", err);
    }
  };

  const submitRejection = useCallback(async () => {
    if (!selectedEnquiry?.enquiry_id || !rejectionReason.trim()) {
      toast.warning("Rejection reason is required.");
      return;
    }
    try {
      await rejectEnquiry(
        selectedEnquiry.enquiry_id,
        rejectionReason,
        empToken
      );
      const updatedEnquiries = await getAllEnquiries(empToken);
      setEnquiries(updatedEnquiries);
      setUpdateModalVisible(false);
      setShowRejectReason(false);
      setRejectionReason("");
    } catch (error) {
      console.error("Error rejecting enquiry:", error);
    }
  }, [selectedEnquiry, rejectionReason]);

  const submitHold = async () => {
    if (!holdReason.trim()) {
      toast.warning("Please enter a reason for On Hold");
      return;
    }

    try {
      await holdEnquiry(selectedEnquiry?.enquiry_id, holdReason, empToken);
      const updatedEnquiries = await getAllEnquiries(empToken);
      setEnquiries(updatedEnquiries);
      setShowHoldReason(false);
      setHoldReason("");
      setUpdateModalVisible(false);
      //fetchEnquiries(); // if you use this to refresh
    } catch (error) {
      console.error("Failed to put enquiry on hold", error);
    }
  };

  const [tasks, setTasks] = useState<any[]>([]);

  useEffect(() => {
    fetchTasks();
    fetchTickets();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const data = await getAllTasks(empToken);
      if (Array.isArray(data)) {
        setTasks(data);
      } else {
        setTasks([]);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      const data = await getAllTickets(empToken);
      setTickets(data);
    } catch (err) {
      console.error("Error fetching tickets", err);
    }
  };

  const handleCreateTask = async (values: TaskPayload) => {
    try {
      const payload: TaskPayload = {
        ...values,
        view: 1,
        due_date: values.due_date
          ? (values.due_date as any).format("YYYY-MM-DD")
          : "",
        created_by: EmpId,
        Assign_to: values.Assign_to,
      };

      const formData = new FormData();
      // ✅ Always append Assign_to explicitly
      formData.append("Assign_to", payload.Assign_to);
      // Append each field from the payload
      Object.entries(payload).forEach(([key, value]) => {
        if (key !== "Assign_to" && value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });

      // Append file if exists
      // if (uploadFile) {
      //   formData.append("upload_file", uploadFile);
      // }

      await createTask(empToken, formData);
      fetchTasks();
      taskForm.resetFields();
      // setUploadFile(null);
      setAddTaskModalVisible(false);
      setTicketId(null);
      setSelectedTask(null);
      setSelectedTeam([]);

      // ✅ Mark ticket as having a task (for UI only)
      if (ticketId) {
        setTicketsWithTasks((prev) => {
          if (!prev.includes(ticketId)) {
            return [...prev, ticketId];
          }
          return prev;
        });
      }

      setTicketId(null);
    } catch (error) {
      console.error("Create Task Error:", error);
    }
  };

  const [selectedTeam, setSelectedTeam] = useState<string[]>([]);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Tickets</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Tickets</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2 justify-content-end">
            {/* Export Dropdown */}
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="export-single-ticket"
                    icon={<FileExcelOutlined />}
                    onClick={() => {
                      if (selectedTicketIds.length > 0) {
                        exportSingleTicketExcel(selectedTicketIds[0], empToken);
                      } else {
                        toast.warn("Please select a ticket first");
                      }
                    }}
                  >
                    Export Single Ticket (Excel)
                  </Menu.Item>
                  <Menu.Item
                    key="export-all-tickets"
                    icon={<FileExcelOutlined />}
                    onClick={() => exportAllTicketsExcel(empToken)}
                  >
                    Export All Tickets (Excel)
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <Button
                type="primary"
                icon={<ExportOutlined />}
                className="btn btn-primary d-flex justify-content-center align-items-center p-2 rounded"
              />
            </Dropdown>
            <Button
              className={`px-4 py-2 rounded ${
                activeTab === "tickets"
                  ? "btn-light text-primary border border-primary"
                  : "btn-primary text-white"
              }`}
              onClick={() => handleTabClick("tickets")}
            >
              <span>Tickets</span>
            </Button>
            <Button
              className={`px-4 py-2 rounded ${
                activeTab === "enquiry"
                  ? "btn-light text-primary border border-primary"
                  : "btn-primary text-white"
              }`}
              onClick={() => handleTabClick("enquiry")}
            >
              <span>Enquiry Details</span>
            </Button>
            <div>
              <CollapseHeader />
            </div>
          </div>
        </div>

        {activeTab === "tickets" ? (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Tickets</h5>
              <Input.Search
                placeholder="Search name..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: 300 }}
              />
            </div>
            <div className="card-body p-0">
              <Table
                dataSource={filteredTickets}
                columns={columns1}
                rowKey="ticket_id" // or "id", depending on your data
                Selection={true}
                scroll={{ x: 1500 }}
                rowSelection={{
                  selectedRowKeys: selectedTicketIds,
                  onChange: (selectedRowKeys) =>
                    setSelectedTicketIds(selectedRowKeys as string[]),
                }}
              />
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Enquiry Details Table</h5>
              <Input.Search
                placeholder="Search name..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: 300 }}
              />
            </div>
            <div className="card-body p-0">
              <Table
                dataSource={filteredEnquiries}
                columns={columns}
                loading={loading}
                scroll={{ x: 1500 }}
                onRow={(record) => ({
                  onClick: (e) => {
                    // Only set selected enquiry if not clicking on buttons
                    const target = e.target as HTMLElement;
                    if (!target.closest(".text-white.rounded.px-2.py-1")) {
                      setSelectedEnquiry(record);
                    }
                  },
                })}
              />
            </div>
          </div>
        )}

        {/* Update Modal */}
        {updateModalVisible && selectedEnquiry && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={() => {
              setAssignModalVisible(false);
              setUpdateModalVisible(false);
              setTicketEnquiryId(null);
              setSelectedEnquiry(null);
              setShowRejectReason(false);
              setShowHoldReason(false);
              setRejectionReason("");
              setHoldReason("");
            }}
          >
            <div
              style={{
                width: 600,
                backgroundColor: "#fff",
                borderRadius: 12,
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                overflow: "hidden",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  background: "#1e3a8a",
                  color: "white",
                  padding: "16px 20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  minHeight: 56,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 600,
                    color: "white",
                  }}
                >
                  Update Enquiry
                </h3>
                <button
                  onClick={() => {
                    setAssignModalVisible(false);
                    setUpdateModalVisible(false);
                    setTicketEnquiryId(null);
                    setSelectedEnquiry(null);
                    setShowRejectReason(false);
                    setShowHoldReason(false);
                    setRejectionReason("");
                    setHoldReason("");
                  }}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "50%",
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "white",
                    fontSize: 18,
                    transition: "all 0.3s ease",
                    backdropFilter: "blur(10px)",
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background =
                      "rgba(255, 255, 255, 0.2)";
                    (e.target as HTMLElement).style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background =
                      "rgba(255, 255, 255, 0.1)";
                    (e.target as HTMLElement).style.transform = "scale(1)";
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Content */}
              <div
                style={{
                  padding: "24px",
                  overflowY: "auto",
                  flex: 1,
                }}
              >
                {selectedEnquiry && (
                  <div className="row">
                    {/* Enquiry Details */}
                    <div className="col-md-6 mb-2">
                      <strong>ID: </strong> {selectedEnquiry.enquiry_id}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Status: </strong>{" "}
                      <Tag
                        bordered
                        style={{
                          border: `1px solid ${
                            statusColors[selectedEnquiry.status!]?.color ||
                            "#0d6efd"
                          }`,
                          color:
                            statusColors[selectedEnquiry.status!]?.color ||
                            "#0d6efd",
                          backgroundColor: "transparent",
                          textTransform: "capitalize",
                          fontSize:
                            window.innerWidth < 576
                              ? "10px"
                              : window.innerWidth < 768
                              ? "11px"
                              : "12px",
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: "16px",
                          gap: "6px",
                        }}
                      >
                        <span>
                          {statusColors[selectedEnquiry.status!]?.icon || "✔️"}
                        </span>
                        <span>{selectedEnquiry.status || "Pending"}</span>
                      </Tag>
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Company: </strong> {selectedEnquiry.company_name}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Licence: </strong>{" "}
                      {selectedEnquiry.company_license || "Not updated"}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Email: </strong>{" "}
                      {selectedEnquiry.email || "Not updated"}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Work Category: </strong>{" "}
                      {categoryMap[selectedEnquiry.category] || "Not updated"}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Product: </strong>{" "}
                      {productMap[selectedEnquiry.product_id]?.name ||
                        "Not updated"}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Modules: </strong>{" "}
                      {moduleMap[selectedEnquiry.module_id] || "Not updated"}
                    </div>
                    <div className="col-md-12 mt-3">
                      <strong>Requirement: </strong>
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          padding: "10px",
                          borderRadius: "5px",
                          marginTop: "5px",
                          maxHeight: "150px",
                          overflowY: "auto",
                          backgroundColor: "#f8f9fa",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedEnquiry.requirement}
                      </div>
                    </div>
                  </div>
                )}

                {/* Conditional Rendering Based on Status */}
                {selectedEnquiry?.status?.toLowerCase() === "rejected" ? (
                  <div className="mt-3">
                    <strong>Rejection Reason: </strong>
                    <div
                      style={{
                        border: "1px solid #dee2e6",
                        padding: "10px",
                        borderRadius: "5px",
                        marginTop: "5px",
                        maxHeight: "150px",
                        overflowY: "auto",
                        backgroundColor: "#f8f9fa",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedEnquiry.rejection_reason || "No reason provided"}
                    </div>
                  </div>
                ) : !showRejectReason && !showHoldReason ? (
                  <>
                    {selectedEnquiry?.status?.toLowerCase() === "on hold" && (
                      <div className="mt-3">
                        <strong>Hold Reason: </strong>
                        <div
                          style={{
                            border: "1px solid #dee2e6",
                            padding: "10px",
                            borderRadius: "5px",
                            marginTop: "5px",
                            maxHeight: "150px",
                            overflowY: "auto",
                            backgroundColor: "#f8f9fa",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {selectedEnquiry.rejection_reason ||
                            "No reason provided"}
                        </div>
                      </div>
                    )}
                    {(selectedEnquiry?.status?.toLowerCase() === "pending" ||
                      selectedEnquiry?.status?.toLowerCase() === "on hold") && (
                      <div className="mt-3">
                        <Space>
                          <Button type="primary" onClick={handleApprove}>
                            Approve
                          </Button>
                          <Button danger onClick={handleReject}>
                            Reject
                          </Button>
                          <Button
                            style={{
                              backgroundColor: "#ffc107",
                              color: "#000",
                            }}
                            onClick={handleHold}
                          >
                            On Hold
                          </Button>
                        </Space>
                      </div>
                    )}
                  </>
                ) : showRejectReason ? (
                  <div className="mt-3">
                    <TextArea
                      rows={4}
                      placeholder="Enter rejection reason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                    />
                    <Button
                      className="mt-2"
                      type="primary"
                      danger
                      onClick={submitRejection}
                    >
                      Submit Rejection
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3">
                    <TextArea
                      rows={4}
                      placeholder="Enter reason for putting on hold"
                      value={holdReason}
                      onChange={(e) => setHoldReason(e.target.value)}
                    />
                    <Button
                      className="mt-2"
                      style={{ backgroundColor: "#ffc107", color: "#000" }}
                      onClick={submitHold}
                    >
                      Submit Hold
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Assign Drawer */}
        <Drawer
          title="Add Ticket"
          placement="right"
          open={assignModalVisible}
          onClose={() => {
            setAssignModalVisible(false);
            setUpdateModalVisible(false);
            setTicketEnquiryId(null);
            resetModalsAndForms();
            setSelectedEnquiry(null);
            setShowRejectReason(false);
            setRejectionReason("");
            ticketForm.resetFields();
          }}
          width={480}
          styles={{
            header: {
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "#fff",
            },
            body: {
              paddingBottom: 80,
              maxHeight: "calc(100vh - 110px)",
              overflowY: "auto",
            },
          }}
          footer={
            <div style={{ textAlign: "right" }}>
              <Button
                onClick={() => {
                  const currentEnquiryId =
                    ticketForm.getFieldValue("enquiry_id");
                  ticketForm.resetFields();
                  ticketForm.setFieldsValue({ enquiry_id: currentEnquiryId });
                }}
                style={{ marginRight: 8 }}
              >
                Reset
              </Button>
              <Button type="primary" onClick={() => ticketForm.submit()}>
                Submit
              </Button>
            </div>
          }
        >
          <Form
            layout="vertical"
            form={ticketForm}
            onFinish={handleAddTicketSubmit}
          >
            <Form.Item
              name="enquiry_id"
              label="Enquiry ID"
              initialValue={ticketEnquiryId}
              rules={[
                { required: true, message: "Please enter an enquiry ID" },
              ]}
            >
              <Input
                disabled={!!ticketEnquiryId}
                placeholder="Enter Enquiry ID"
                style={{
                  color: "#000",
                  borderColor: "#444",
                  opacity: 1,
                }}
              />
            </Form.Item>

            <Form.Item
              name="ticket_title"
              label="Ticket Title"
              rules={[{ required: true, message: "Please enter ticket title" }]}
            >
              <Input placeholder="Enter ticket title" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true }]}
            >
              <TextArea
                rows={4}
                placeholder="Enter description"
                disabled
                style={{
                  color: "#000",
                  borderColor: "#444",
                  opacity: 1,
                }}
              />
            </Form.Item>

            <Form.Item
              name="category"
              label="Work Category"
              rules={[{ required: true, message: "Category is required" }]}
            >
              <Input
                disabled
                value={
                  selectedEnquiry?.category
                    ? categoryMap[selectedEnquiry.category] || ""
                    : undefined
                }
                placeholder="Category"
                style={{ color: "#000" }}
              />
            </Form.Item>

            <Form.Item
              name="priority"
              label="Priority"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select priority">
                <Select.Option value="High">High</Select.Option>
                <Select.Option value="Medium">Medium</Select.Option>
                <Select.Option value="Low">Low</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="assigned_to"
              label="Assigned To"
              rules={[{ required: true, message: "Please select an employee" }]}
            >
              <Select
                showSearch
                placeholder="Select employee"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString().toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
                style={{ width: "100%" }}
              >
                {filteredEmployees.map((emp) => (
                  <Select.Option key={emp.employee_id} value={emp.employee_id}>
                    {`${emp.firstname} ${emp.lastname}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Drawer>

        {/* Modal for displaying Requirement */}
        <Modal
          title="Requirement Details"
          visible={isModalVisible}
          onCancel={() => {
            setAssignModalVisible(false);
            setUpdateModalVisible(false);
            setTicketEnquiryId(null);
            setSelectedEnquiry(null);
            setShowRejectReason(false);
            setRejectionReason("");
            //form.resetFields();
          }}
          footer={null}
          width={700}
          bodyStyle={{
            maxHeight: "400px",
            overflowY: "auto",
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "6px",
          }}
        >
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            <Paragraph style={{ fontSize: 15 }}>
              {currentRequirement || "N/A"}
            </Paragraph>
          </div>
        </Modal>
        <Drawer
          title="Add Task"
          placement="right"
          open={addTaskModalVisible}
          onClose={() => {
            setAddTaskModalVisible(false);
            taskForm.resetFields();
            setSelectedTask(null);
            setSelectedTeam([]);
            setTicketId(null);
          }}
          width={480} // ✅ narrower drawer
          styles={{
            header: {
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "#fff",
            }, // sticky title
            body: {
              paddingBottom: 80,
              maxHeight: "calc(100vh - 110px)",
              overflowY: "auto",
            }, // scrollable body
          }}
          footer={
            <div style={{ textAlign: "right" }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  const currentTicketId = taskForm.getFieldValue("ticket_id");
                  taskForm.resetFields();
                  taskForm.setFieldsValue({ ticket_id: currentTicketId });
                }}
              >
                Reset
              </Button>
              <Button type="primary" onClick={() => taskForm.submit()}>
                Submit
              </Button>
            </div>
          }
        >
          <Form layout="vertical" form={taskForm} onFinish={handleCreateTask}>
            <Form.Item
              name="task_name"
              label="Task Name"
              rules={[{ required: true, message: "Please enter task name" }]}
            >
              <Input
                disabled
                placeholder="Enter task name"
                style={{ color: "#000" }}
              />
            </Form.Item>

            <Form.Item
              name="ticket_id"
              label="Ticket ID"
              rules={[{ required: true, message: "Ticket ID is required" }]}
            >
              <Input disabled style={{ color: "#000" }} />
            </Form.Item>

            <Form.Item
              name="Assign_to"
              label="Assign To"
              rules={[{ required: true, message: "Please select an employee" }]}
            >
              <Select
                showSearch
                placeholder="Select employee"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString().toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
              >
                {filteredDevelopmentEmployees.map((emp) => (
                  <Select.Option key={emp.employee_id} value={emp.employee_id}>
                    {emp.firstname} {emp.lastname}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="priority"
              label="Priority"
              rules={[{ required: true, message: "Please select priority" }]}
            >
              <Select placeholder="Select priority">
                <Select.Option value="High">High</Select.Option>
                <Select.Option value="Medium">Medium</Select.Option>
                <Select.Option value="Low">Low</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              initialValue="Yet To Start"
              hidden
            >
              <Input disabled />
            </Form.Item>

            <Form.Item
              name="due_date"
              label="Due Date"
              rules={[{ required: true, message: "Please select due date" }]}
            >
              <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="estimated_hours"
              label="Estimated Hours"
              rules={[
                { required: true, message: "Please enter estimated hours" },
              ]}
            >
              <Input type="number" min={0} placeholder="Enter hours" />
            </Form.Item>

            <Form.Item
              name="category"
              label="Work Category"
              rules={[{ required: true, message: "Category is required" }]}
            >
              <Input
                disabled
                value={
                  selectedEnquiry?.category
                    ? categoryMap[selectedEnquiry.category]
                    : undefined
                }
                placeholder="Category"
                style={{ color: "#000" }}
              />
            </Form.Item>
            <Form.Item label="Requirement Details">
              <div
                style={{
                  border: "1px solid #ccc",
                  padding: "12px",
                  borderRadius: "6px",
                  backgroundColor: "#f9f9f9",
                  minHeight: "80px",
                }}
              >
                <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                  {selectedEnquiry?.requirement || "No requirement available"}
                </p>
              </div>
            </Form.Item>
          </Form>
        </Drawer>
      </div>
      {/* Footer */}
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2024 - 2025 © IATT.</p>
        <p>
          Designed & Developed By{" "}
          <Link to="#" className="text-primary">
            IATT
          </Link>
        </p>
      </div>

      {ticketDetailsModalVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => {
            setTicketDetailsModalVisible(false);
            setSelectedTicketForDetails(null);
          }}
        >
          <div
            style={{
              width: 600,
              backgroundColor: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                background: "#1e3a8a",
                color: "white",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: 56,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: "white",
                }}
              >
                Ticket Details
              </h3>
              <button
                onClick={() => {
                  setTicketDetailsModalVisible(false);
                  setSelectedTicketForDetails(null);
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  fontSize: 18,
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.2)";
                  (e.target as HTMLElement).style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background =
                    "rgba(255, 255, 255, 0.1)";
                  (e.target as HTMLElement).style.transform = "scale(1)";
                }}
              >
                ✕
              </button>
            </div>
            {/* Content */}
            <div
              style={{
                padding: "24px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {selectedTicketForDetails ? (
                <div>
                  {/* Ticket ID */}
                  <div style={{ marginBottom: 20 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Ticket ID :{" "}
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: "#10b981",
                          color: "white",
                          padding: "4px 12px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {selectedTicketForDetails.ticket_id}
                      </span>
                    </span>
                  </div>
                  {/* Title */}
                  <div style={{ marginBottom: 20 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Title :{" "}
                      <span
                        style={{
                          fontSize: 14,
                          color: "#6b7280",
                          padding: "8px 0",
                        }}
                      >
                        {selectedTicketForDetails.ticket_title || "N/A"}
                      </span>
                    </label>
                  </div>
                  {/* Enquiry ID */}
                  <div style={{ marginBottom: 20 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Enquiry ID :{" "}
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: "#10b981",
                          color: "white",
                          padding: "4px 12px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {selectedTicketForDetails.enquiry_id}
                      </span>
                    </label>
                  </div>
                  {/* Company */}
                  <div style={{ marginBottom: 20 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Company :{" "}
                      <span
                        style={{
                          fontSize: 14,
                          color: "#6b7280",
                          padding: "8px 0",
                        }}
                      >
                        {selectedTicketForDetails.enquiry?.company_name}
                      </span>
                    </label>
                  </div>
                  {/* Product */}
                  <div style={{ marginBottom: 20 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Product :{" "}
                      <span
                        style={{
                          fontSize: 14,
                          color: "#6b7280",
                          padding: "8px 0",
                        }}
                      >
                        {(() => {
                          const productId =
                            selectedTicketForDetails.enquiry?.product_id;
                          return productId !== undefined &&
                            productMap[productId]
                            ? productMap[productId].name
                            : "Not Updated";
                        })()}
                      </span>
                    </label>
                  </div>
                  {/* Module */}
                  <div style={{ marginBottom: 20 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Module :{" "}
                      <span
                        style={{
                          fontSize: 14,
                          color: "#6b7280",
                          padding: "8px 0",
                        }}
                      >
                        {(() => {
                          const moduleId =
                            selectedTicketForDetails.enquiry?.module_id;
                          return moduleId !== undefined && moduleMap[moduleId]
                            ? moduleMap[moduleId]
                            : "Not Updated";
                        })()}
                      </span>
                    </label>
                  </div>
                  {/* Work Category */}
                  <div style={{ marginBottom: 20 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Work Category :{" "}
                      <span
                        style={{
                          fontSize: 14,
                          color: "#6b7280",
                          padding: "8px 0",
                        }}
                      >
                        {selectedTicketForDetails?.enquiry?.category !==
                        undefined
                          ? categoryMap[
                              selectedTicketForDetails.enquiry.category
                            ] || "N/A"
                          : "N/A"}
                      </span>
                    </label>
                  </div>
                  {/* Description */}
                  <div style={{ marginBottom: 0 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 6,
                      }}
                    >
                      Description :
                    </label>
                    <div
                      style={{
                        padding: 16,
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        fontSize: 14,
                        color: "#6b7280",
                        lineHeight: 1.6,
                        maxHeight: 150,
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedTicketForDetails.enquiry?.requirement || "N/A"}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: 40,
                    color: "#9ca3af",
                  }}
                >
                  No ticket selected
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default TmsTickets;
